import { useMemo, useRef, type ComponentPropsWithoutRef } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  blogMarkdownComponents,
  remarkVideoEmbeds,
} from "@/lib/blog/markdown-components";
import { ChevronRight, Clock } from "lucide-react";
import { Layout } from "@/components/Layout";
import { blogPostQueryOptions } from "@/lib/public-content.queries";
import { useHeadings } from "@/lib/blog/use-headings";
import { normalizeArticleHeadings } from "@/lib/blog/article-headings";
import { metaDescription, pageTitle } from "@/lib/seo-text";
import { slugify } from "@/lib/blog/slugify";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import {
  TableOfContentsDesktop,
  TableOfContentsMobile,
} from "@/components/blog/TableOfContents";
import { ShareBar } from "@/components/blog/ShareBar";
import { AuthorBio } from "@/components/blog/AuthorBio";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { BackToTop } from "@/components/blog/BackToTop";
import { PostCTA } from "@/components/blog/PostCTA";
import { retryImageOnce } from "@/lib/storage-image";

export const SITE_ORIGIN = "https://creategy.de";

const formatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

export const Route = createFileRoute("/blog/$slug")({
  // Ohne veröffentlichten Beitrag muss die Antwort einen echten 404 tragen.
  // Sonst liefert die Adresse Status 200 mit leerer Seite – Prüfdienste und
  // Suchmaschinen halten sie dann für eine gültige Seite ohne Inhalt.
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(
      blogPostQueryOptions(params.slug),
    );
    if (!post) throw notFound();
    return post;
  },

  head: ({ loaderData, params }) => {
    const post = loaderData;
    const title = post ? pageTitle(post.title, " – creategy Blog") : "Artikel – creategy Blog";
    const description = metaDescription(
      post?.excerpt ?? "Analysen und Essays zur politischen Kommunikation.",
    );
    const url = `${SITE_ORIGIN}/blog/${params.slug}`;
    const meta = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
    ];
    /* Ticket KI1: die dauerhaft gültige Teilen-Adresse statt der ablaufenden. */
    if (post?.cover_share_url) {
      meta.push({ property: "og:image", content: post.cover_share_url });
      meta.push({ name: "twitter:image", content: post.cover_share_url });
    }
    const links: Array<{ rel: string; href: string; as?: string; fetchpriority?: string }> = [
      { rel: "canonical", href: url },
    ];
    if (post?.cover_url) {
      links.push({
        rel: "preload",
        as: "image",
        href: post.cover_url,
        fetchpriority: "high",
      });
    }
    const scripts: Array<{ type: string; children: string }> = [];
    if (post) {
      const date = post.published_at ?? post.created_at;
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt ?? undefined,
          image: post.cover_share_url ?? undefined,
          datePublished: date,
          author: post.author
            ? { "@type": "Person", name: post.author }
            : { "@type": "Organization", name: "creategy GmbH" },
          publisher: {
            "@type": "Organization",
            name: "creategy GmbH",
          },
          mainEntityOfPage: url,
        }),
      });
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Blog", item: `${SITE_ORIGIN}/blog` },
            { "@type": "ListItem", position: 2, name: post.category },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      });
    }
    return { meta, links, scripts };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Artikel nicht gefunden.</h1>
        <Link to="/blog" className="mt-6 inline-block text-sm font-semibold underline">
          Zurück zur Übersicht
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Beitrag konnte nicht geladen werden.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Bitte versuchen Sie es später erneut.
        </p>
        <Link to="/blog" className="mt-6 inline-block text-sm font-semibold underline">
          Zurück zur Übersicht
        </Link>
      </div>
    </Layout>
  ),
  component: PostPage,
});

function headingText(children: unknown): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(headingText).join("");
  if (children && typeof children === "object" && "props" in (children as object)) {
    return headingText((children as { props: { children: unknown } }).props.children);
  }
  return "";
}

function H2(props: ComponentPropsWithoutRef<"h2">) {
  const id = slugify(headingText(props.children));
  return <h2 {...props} id={id} className="heading-prose-h2 scroll-mt-24" />;
}
function H3(props: ComponentPropsWithoutRef<"h3">) {
  const id = slugify(headingText(props.children));
  return <h3 {...props} id={id} className="heading-prose-h3 scroll-mt-24" />;
}

function PostPage() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(blogPostQueryOptions(slug));
  const articleRef = useRef<HTMLElement | null>(null);
  // Der Titel ist die einzige H1 der Seite, siehe article-headings.ts.
  const body = useMemo(() => normalizeArticleHeadings(post?.content_md ?? ""), [post?.content_md]);
  const headings = useHeadings(body);

  if (!post) {
    throw notFound();
  }

  const date = post.published_at ?? post.created_at;
  const shareUrl = `${SITE_ORIGIN}/blog/${post.slug}`;

  return (
    <Layout>
      <ReadingProgress targetRef={articleRef} />

      {/* Header */}
      <header className="border-b border-border bg-secondary/60">
        <div className="mx-auto max-w-5xl px-6 md:px-12 section-y-hero">
          <nav
            aria-label="Brotkrumen"
            className="flex flex-wrap items-center gap-1.5 font-body text-xs text-muted-foreground"
          >
            <Link to="/blog" className="hover:text-foreground">
              Blog
            </Link>
            <ChevronRight className="h-3 w-3" aria-hidden />
            <span className="text-foreground">{post.category}</span>
          </nav>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary px-3 py-1 font-display text-xs font-extrabold uppercase tracking-wide text-primary-foreground">
              {post.category}
            </span>
            {post.reading_time && (
              <span className="inline-flex items-center gap-1.5 font-body text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" aria-hidden /> {post.reading_time}
              </span>
            )}
            <span className="font-body text-xs text-muted-foreground">
              {formatter.format(new Date(date))}
            </span>
          </div>

          <h1 className="heading-h1 mt-6 max-w-4xl">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="prose-lead mt-6 max-w-3xl text-foreground">
              {post.excerpt}
            </p>
          )}

          <div className="mt-10 flex flex-col gap-6 border-t border-border pt-6 md:flex-row md:items-center md:justify-between lg:hidden">
            <div className="flex items-center gap-3">
              {post.author_image_url ? (
                <img
                  src={post.author_image_url}
                  alt={post.author || "Autor"}
                  width={44}
                  height={44}
                  loading="lazy"
                  decoding="async"
                  className="h-11 w-11 rounded-full object-cover"
                />
              ) : (
                <div className="h-11 w-11 rounded-full bg-background" />
              )}
              {post.author && (
                <div className="font-body text-sm">
                  <p className="text-muted-foreground">Von</p>
                  <p className="font-display font-extrabold text-primary">
                    {post.author}
                  </p>
                </div>
              )}
            </div>
            <ShareBar title={post.title} url={shareUrl} />
          </div>
        </div>
      </header>

      {/* Cover */}
      {post.cover_url && (
        <div className="mx-auto max-w-5xl px-6 md:px-12 -mt-8 md:-mt-12">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-gradient-to-br from-secondary to-accent shadow-xl">
            <img
              src={post.cover_url}
              onError={retryImageOnce}
              alt={post.title}
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Content grid */}
      <article ref={articleRef} className="bg-background">
        <div className="mx-auto max-w-6xl px-6 md:px-12 section-y">
          <div className="grid gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
            {/* Left rail: TOC + Share desktop */}
            <aside aria-label="Inhaltsverzeichnis" className="hidden lg:block">
              <div className="sticky top-24 z-10 flex max-h-[calc(100vh-7rem)] flex-col gap-8 overflow-hidden bg-background pb-4">
                <TableOfContentsDesktop headings={headings} />
                <div className="shrink-0 border-t border-border pt-6">
                  <p className="mb-3 font-display text-xs font-extrabold uppercase tracking-wide text-primary">
                    Teilen
                  </p>
                  <ShareBar title={post.title} url={shareUrl} />
                </div>
              </div>
            </aside>

            {/* Article body */}
            <div className="min-w-0">
              <TableOfContentsMobile headings={headings} />
              <div className="markdown-body markdown-article">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkVideoEmbeds]}
                  components={{ ...blogMarkdownComponents, h2: H2, h3: H3 }}
                >
                  {body}
                </ReactMarkdown>
              </div>

              {(post.tags ?? []).length > 0 && (
                <div className="mt-12 flex flex-wrap gap-2 border-t border-border pt-8">
                  {(post.tags ?? []).map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      <AuthorBio name={post.author} imageUrl={post.author_image_url} bio={post.author_bio} />
      <RelatedPosts category={post.category} excludeSlug={post.slug} />
      <PostCTA />
      <BackToTop />
    </Layout>
  );
}
